import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import {
  sourcingApi,
  type SearchProject,
  type SourcedCandidateDetail,
  type SourcingScore,
} from '@/api/sourcing';
import type { Vacancy } from '@/types';
import { GradeBadge } from '../detail/page';

const OUTREACH_STATUSES = [
  'found', 'reviewed', 'contacted', 'replied', 'interested', 'questionnaire',
  'case', 'interview', 'finalist', 'offer', 'rejected', 'no_response',
];
const CHANNELS = ['linkedin_message', 'linkedin_inmail', 'email', 'referral'];

interface RawExperience {
  position?: string;
  title?: string;
  companyName?: string;
  company?: string;
  duration?: string;
  dates?: string;
  description?: string;
}

export default function SourcingCandidate() {
  const { t } = useTranslation();
  const { id: projectId, cid } = useParams<{ id: string; cid: string }>();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState<SourcedCandidateDetail | null>(null);
  const [project, setProject] = useState<SearchProject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [scoring, setScoring] = useState(false);
  const [showPromote, setShowPromote] = useState(false);

  useEffect(() => {
    if (!cid) return;
    sourcingApi
      .getCandidate(cid)
      .then((c) => {
        setCandidate(c);
        setNotes(c.notes || '');
      })
      .catch((e) => setError(e.message));
  }, [cid]);

  useEffect(() => {
    if (!projectId) return;
    sourcingApi.getProject(projectId).then(setProject).catch(() => {});
  }, [projectId]);

  const score: SourcingScore | undefined = candidate?.scores?.[candidate.scores.length - 1]
    ? candidate.scores.find((s) => s.score_version === 1) || candidate.scores[0]
    : undefined;

  const raw = candidate?.raw_json as Record<string, unknown> | null;
  const experience = useMemo(
    () => ((raw?.experience || raw?.experiences || []) as RawExperience[]),
    [raw]
  );
  const skills = useMemo(() => {
    const s = (raw?.skills || []) as ({ name?: string } | string)[];
    return s.map((x) => (typeof x === 'string' ? x : x.name)).filter(Boolean) as string[];
  }, [raw]);
  const languages = useMemo(() => {
    const l = (raw?.languages || []) as ({ language?: string; name?: string; proficiency?: string } | string)[];
    return l
      .map((x) => (typeof x === 'string' ? x : [x.language || x.name, x.proficiency].filter(Boolean).join(' — ')))
      .filter(Boolean) as string[];
  }, [raw]);
  const education = useMemo(() => {
    const e = (raw?.education || raw?.educations || []) as { schoolName?: string; school?: string; degree?: string; subtitle?: string }[];
    return e.map((x) => [x.schoolName || x.school, x.degree || x.subtitle].filter(Boolean).join(' — '));
  }, [raw]);

  const patch = async (body: Record<string, unknown>) => {
    if (!cid) return;
    try {
      await sourcingApi.patchCandidate(cid, body);
      const fresh = await sourcingApi.getCandidate(cid);
      setCandidate(fresh);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const scoreNow = async () => {
    if (!cid) return;
    setScoring(true);
    setError(null);
    try {
      await sourcingApi.scoreCandidate(cid);
      setCandidate(await sourcingApi.getCandidate(cid));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setScoring(false);
    }
  };

  const copyOutreach = async () => {
    const template = project?.candidate_profile?.outreach_template;
    if (!template) {
      setNotice(t('sourcing.noTemplate'));
      return;
    }
    const firstName = (candidate?.full_name || '').split(' ')[0] || '';
    const text = template
      .replaceAll('{{name}}', firstName)
      .replaceAll('{{full_name}}', candidate?.full_name || '');
    await navigator.clipboard.writeText(text);
    setNotice(t('sourcing.copied'));
    setTimeout(() => setNotice(null), 2000);
  };

  if (!candidate) {
    return (
      <p className="text-sm text-foreground-400">
        {error || t('sourcing.loading')}
      </p>
    );
  }

  const effGrade = candidate.manual_grade || score?.grade || null;

  return (
    <div>
      <button
        onClick={() => navigate(`/sourcing/${projectId}`)}
        className="text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer mb-3 flex items-center gap-1"
      >
        <i className="ri-arrow-left-line" /> {t('sourcing.back')}
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-heading font-bold text-foreground-950">
              {candidate.full_name || '—'}
            </h1>
            {effGrade && <GradeBadge grade={effGrade} manual={!!candidate.manual_grade} />}
            {score?.total_score != null && (
              <span className="font-mono font-bold text-foreground-800">{score.total_score}/100</span>
            )}
          </div>
          <p className="text-sm text-foreground-500 mt-1">{candidate.headline}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-400 mt-1 flex-wrap">
            {candidate.location_text && <span>{candidate.location_text}</span>}
            {candidate.connections_count != null && (
              <span>{candidate.connections_count} {t('sourcing.connections')}</span>
            )}
            <a
              href={candidate.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 hover:underline flex items-center gap-1"
              onClick={() => {
                if (candidate.outreach_status === 'found') patch({ outreach_status: 'reviewed' });
              }}
            >
              <i className="ri-linkedin-box-fill" /> LinkedIn
            </a>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={copyOutreach}
            className="px-3 py-2 rounded-xl border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-file-copy-line" /> {t('sourcing.copyOutreach')}
          </button>
          {candidate.promoted_candidate_id ? (
            <button
              onClick={() => navigate(`/candidates/${candidate.promoted_candidate_id}`)}
              className="px-3 py-2 rounded-xl bg-green-100 text-green-700 text-xs font-medium cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-check-line" /> {t('sourcing.promoted')}
            </button>
          ) : (
            <button
              onClick={() => setShowPromote(true)}
              className="px-3 py-2 rounded-xl bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-arrow-right-up-line" /> {t('sourcing.promote')}
            </button>
          )}
        </div>
      </div>

      {notice && <p className="text-sm text-primary-600 mb-2">{notice}</p>}
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: profile */}
        <div className="space-y-4">
          <Section title={t('sourcing.profile')}>
            <div className="flex gap-1.5 flex-wrap mb-3">
              {candidate.past_founder && <Chip label={t('sourcing.flagFounder')} />}
              {candidate.fast_growth && <Chip label={t('sourcing.flagFastGrowth')} />}
              {candidate.speaks_russian && <Chip label={t('sourcing.flagRussian')} />}
              {candidate.open_to_work && <Chip label={t('sourcing.flagOpenToWork')} />}
              {candidate.bucket && <Chip label={`bucket ${candidate.bucket}`} />}
            </div>
            {typeof raw?.about === 'string' && raw.about && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-foreground-400 font-medium mb-1">
                  {t('sourcing.about')}
                </p>
                <p className="text-sm text-foreground-700 whitespace-pre-line mb-3">{String(raw.about)}</p>
              </>
            )}
            <p className="text-[10px] uppercase tracking-wider text-foreground-400 font-medium mb-1">
              {t('sourcing.experience')}
            </p>
            <div className="space-y-2.5">
              {experience.map((e, i) => (
                <div key={i} className="border-l-2 border-background-200 pl-3">
                  <p className="text-sm font-medium text-foreground-800">
                    {e.position || e.title} · {e.companyName || e.company}
                  </p>
                  {(e.duration || e.dates) && (
                    <p className="text-xs text-foreground-400">{e.duration || e.dates}</p>
                  )}
                  {e.description && (
                    <p className="text-xs text-foreground-600 mt-1 line-clamp-3">{e.description}</p>
                  )}
                </div>
              ))}
            </div>
            {education.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-foreground-400 font-medium mb-1 mt-3">
                  {t('sourcing.education')}
                </p>
                {education.map((e, i) => (
                  <p key={i} className="text-sm text-foreground-700">{e}</p>
                ))}
              </>
            )}
            {skills.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-foreground-400 font-medium mb-1 mt-3">
                  {t('sourcing.skills')}
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {skills.slice(0, 15).map((s) => (
                    <span key={s} className="text-xs bg-background-100 text-foreground-600 px-2 py-0.5 rounded-full">
                      {s}
                    </span>
                  ))}
                </div>
              </>
            )}
            {languages.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-foreground-400 font-medium mb-1 mt-3">
                  {t('sourcing.languages')}
                </p>
                <p className="text-sm text-foreground-700">{languages.join(', ')}</p>
              </>
            )}
          </Section>

          {/* Manage */}
          <Section title={t('sourcing.outreachStatus')}>
            <div className="flex gap-3 flex-wrap items-end">
              <div>
                <label className="text-xs text-foreground-500 block mb-1">{t('sourcing.manualGrade')}</label>
                <div className="flex gap-1">
                  {['A', 'B', 'C'].map((g) => (
                    <button
                      key={g}
                      onClick={() => patch({ manual_grade: candidate.manual_grade === g ? null : g })}
                      className={`w-9 h-9 rounded-lg text-sm font-bold cursor-pointer border ${
                        candidate.manual_grade === g
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'border-background-200 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">{t('sourcing.outreachStatus')}</label>
                <select
                  value={candidate.outreach_status}
                  onChange={(e) => patch({ outreach_status: e.target.value })}
                  className="text-sm border border-background-200 rounded-lg px-2 py-2 cursor-pointer"
                >
                  {OUTREACH_STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`sourcing.status.${s}`, s)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-foreground-500 block mb-1">{t('sourcing.channel')}</label>
                <select
                  value={candidate.outreach_channel || ''}
                  onChange={(e) => patch({ outreach_channel: e.target.value || null })}
                  className="text-sm border border-background-200 rounded-lg px-2 py-2 cursor-pointer"
                >
                  <option value="">—</option>
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-foreground-500 block mb-1">{t('sourcing.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== (candidate.notes || '') && patch({ notes })}
                rows={3}
                className="w-full text-sm border border-background-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
          </Section>
        </div>

        {/* Right: assessment */}
        <div className="space-y-4">
          <Section
            title={t('sourcing.assessment')}
            action={
              <button
                onClick={scoreNow}
                disabled={scoring}
                className="text-xs text-primary-600 hover:underline disabled:opacity-50 cursor-pointer"
              >
                {scoring ? t('sourcing.scoringInProgress') : t('sourcing.scoreNow')}
              </button>
            }
          >
            {!score && <p className="text-sm text-foreground-400">{t('sourcing.notScored')}</p>}
            {score?.summary_ru && (
              <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 mb-4">
                <p className="text-[10px] uppercase tracking-wider text-purple-500 font-medium mb-1">
                  {t('sourcing.summary')}
                </p>
                <p className="text-sm text-foreground-800">{score.summary_ru}</p>
              </div>
            )}
            {score?.criteria && (
              <div className="space-y-3">
                {Object.entries(score.criteria).map(([key, c]) => (
                  <div key={key}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground-800">
                        {t(`sourcing.criteriaLabels.${key}`, key)}
                      </p>
                      <span className="font-mono text-sm font-semibold text-foreground-700">
                        {c.score}/{c.max}
                      </span>
                    </div>
                    <div className="h-1.5 bg-background-100 rounded-full overflow-hidden mb-1.5">
                      <div
                        className={`h-full rounded-full ${
                          c.max && c.score / c.max >= 0.7
                            ? 'bg-green-500'
                            : c.max && c.score / c.max >= 0.45
                              ? 'bg-amber-500'
                              : 'bg-red-400'
                        }`}
                        style={{ width: `${c.max ? Math.min(100, (c.score / c.max) * 100) : 0}%` }}
                      />
                    </div>
                    <EvidenceBlock label={t('sourcing.evidence')} items={c.evidence} color="text-green-700" />
                    <EvidenceBlock label={t('sourcing.assumptions')} items={c.assumptions} color="text-amber-700" />
                    <EvidenceBlock label={t('sourcing.unknowns')} items={c.unknowns} color="text-foreground-400" />
                  </div>
                ))}
              </div>
            )}
          </Section>

          {score?.red_flags && score.red_flags.length > 0 && (
            <Section title={t('sourcing.redFlags')}>
              <ul className="space-y-1">
                {score.red_flags.map((f, i) => (
                  <li key={i} className="text-sm text-red-700 flex gap-2">
                    <i className="ri-flag-fill text-red-400 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {score?.required_questions && score.required_questions.length > 0 && (
            <Section title={t('sourcing.requiredQuestions')}>
              <ol className="space-y-1.5 list-decimal list-inside">
                {score.required_questions.map((q, i) => (
                  <li key={i} className="text-sm text-foreground-700">{q}</li>
                ))}
              </ol>
            </Section>
          )}
        </div>
      </div>

      {showPromote && (
        <PromoteModal
          defaultVacancyId={project?.vacancy_id || ''}
          onClose={() => setShowPromote(false)}
          onPromote={async (vacancyId) => {
            try {
              await sourcingApi.promote(candidate.id, vacancyId);
              setShowPromote(false);
              setNotice(t('sourcing.promotedOk'));
              setCandidate(await sourcingApi.getCandidate(candidate.id));
            } catch (e) {
              setError((e as Error).message);
              setShowPromote(false);
            }
          }}
        />
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-background-200 rounded-2xl px-5 py-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-heading font-semibold text-foreground-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function Chip({ label }: { label: string }) {
  return (
    <span className="text-[11px] font-medium bg-background-100 text-foreground-600 px-2 py-0.5 rounded-full">
      {label}
    </span>
  );
}

function EvidenceBlock({
  label,
  items,
  color,
}: {
  label: string;
  items?: string[];
  color: string;
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-1">
      <p className={`text-[10px] uppercase tracking-wider font-medium ${color}`}>{label}</p>
      <ul className="mt-0.5 space-y-0.5">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-foreground-600">
            · {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PromoteModal({
  defaultVacancyId,
  onClose,
  onPromote,
}: {
  defaultVacancyId: string;
  onClose: () => void;
  onPromote: (vacancyId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [vacancyId, setVacancyId] = useState(defaultVacancyId);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Vacancy[]>('/vacancies').then(setVacancies).catch(() => setVacancies([]));
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
          {t('sourcing.promote')}
        </h2>
        <p className="text-sm text-foreground-500 mb-4">{t('sourcing.promoteConfirm')}</p>
        <label className="text-xs font-medium text-foreground-500 mb-1 block">
          {t('sourcing.selectVacancy')}
        </label>
        <select
          value={vacancyId}
          onChange={(e) => setVacancyId(e.target.value)}
          className="w-full text-sm border border-background-200 rounded-lg px-2 py-2 cursor-pointer mb-4"
        >
          <option value="">—</option>
          {vacancies.map((v) => (
            <option key={v.id} value={v.id}>{v.title}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm text-foreground-600 hover:bg-background-100 cursor-pointer"
          >
            {t('sourcing.cancel')}
          </button>
          <button
            disabled={!vacancyId || busy}
            onClick={async () => {
              setBusy(true);
              await onPromote(vacancyId);
            }}
            className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
          >
            {t('sourcing.promote')}
          </button>
        </div>
      </div>
    </div>
  );
}
